/**
 * Notification Rate Limit Service
 * 
 * Enforces global rate limits on notifications to prevent spam.
 * High priority notifications bypass rate limits.
 */

import AsyncStorage from '@react-native-async-storage/async-storage';

const STORAGE_KEY = '@tripflow_notification_rate_v1';

// Rate limit configuration
export const RATE_LIMITS = {
  // Non-critical notifications (medium/low priority)
  nonCritical: {
    perHour: 5,
    perDay: 15,
  },
  // Low priority has stricter limits
  low: {
    perHour: 3,
    perDay: 10,
  },
};

export type NotificationPriority = 'high' | 'medium' | 'low';

type RateLimitEntry = {
  timestamp: number;
  priority: NotificationPriority;
};

type RateLimitStore = {
  entries: RateLimitEntry[];
  lastPruned: number;
};

// In-memory cache
let memoryCache: RateLimitStore | null = null;
let cacheLoadPromise: Promise<RateLimitStore> | null = null;

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

/**
 * Load the rate limit store from AsyncStorage
 */
const loadStore = async (): Promise<RateLimitStore> => {
  if (memoryCache) {
    return memoryCache;
  }
  
  if (cacheLoadPromise) {
    return cacheLoadPromise;
  }
  
  cacheLoadPromise = (async () => {
    try {
      const raw = await AsyncStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as RateLimitStore;
        if (parsed && Array.isArray(parsed.entries)) {
          memoryCache = parsed;
          return parsed;
        }
      }
    } catch (error) {
      console.error('[NotificationRateLimit] Error loading store:', error);
    }
    
    const emptyStore: RateLimitStore = { entries: [], lastPruned: Date.now() };
    memoryCache = emptyStore;
    return emptyStore;
  })();
  
  const result = await cacheLoadPromise;
  cacheLoadPromise = null;
  return result;
};

/**
 * Save the rate limit store to AsyncStorage
 */
const saveStore = async (store: RateLimitStore): Promise<void> => {
  try {
    memoryCache = store;
    await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.error('[NotificationRateLimit] Error saving store:', error);
  }
};

/**
 * Prune old entries (older than 24 hours)
 */
const pruneOldEntries = async (): Promise<void> => {
  const store = await loadStore();
  const now = Date.now();
  
  // Only prune if it's been at least 1 hour since last prune
  if (now - store.lastPruned < HOUR_MS) {
    return;
  }
  
  const cutoff = now - DAY_MS;
  const validEntries = store.entries.filter((entry) => entry.timestamp > cutoff);
  
  if (validEntries.length !== store.entries.length) {
    await saveStore({
      entries: validEntries,
      lastPruned: now,
    });
  }
};

/**
 * Get counts for the current hour and day
 */
const getCounts = async (priority: NotificationPriority): Promise<{ hourCount: number; dayCount: number }> => {
  const store = await loadStore();
  const now = Date.now();
  const hourAgo = now - HOUR_MS;
  const dayAgo = now - DAY_MS;
  
  // For medium priority, count both medium and low
  // For low priority, count only low
  const relevantEntries = store.entries.filter((entry) => {
    if (priority === 'low') {
      return entry.priority === 'low';
    }
    // Medium counts both medium and low towards limits
    return entry.priority === 'medium' || entry.priority === 'low';
  });
  
  const hourCount = relevantEntries.filter((e) => e.timestamp > hourAgo).length;
  const dayCount = relevantEntries.filter((e) => e.timestamp > dayAgo).length;
  
  return { hourCount, dayCount };
};

/**
 * Check if a notification of the given priority is within rate limits
 * 
 * @param priority - Notification priority level
 * @returns true if the notification is allowed (under rate limit)
 */
export const checkRateLimit = async (priority: NotificationPriority): Promise<boolean> => {
  // High priority always bypasses rate limits
  if (priority === 'high') {
    return true;
  }
  
  const { hourCount, dayCount } = await getCounts(priority);
  
  // Get the appropriate limits based on priority
  const limits = priority === 'low' ? RATE_LIMITS.low : RATE_LIMITS.nonCritical;
  
  // Check both hourly and daily limits
  if (hourCount >= limits.perHour) {
    console.log(`[NotificationRateLimit] Hourly limit reached (${hourCount}/${limits.perHour}) for ${priority}`);
    return false;
  }
  
  if (dayCount >= limits.perDay) {
    console.log(`[NotificationRateLimit] Daily limit reached (${dayCount}/${limits.perDay}) for ${priority}`);
    return false;
  }
  
  return true;
};

/**
 * Record that a notification was sent
 * 
 * @param priority - Priority of the sent notification
 */
export const recordSent = async (priority: NotificationPriority): Promise<void> => {
  const store = await loadStore();
  
  store.entries.push({
    timestamp: Date.now(),
    priority,
  });
  
  await saveStore(store);
  
  // Opportunistically prune old entries
  pruneOldEntries().catch(() => {});
};

/**
 * Check rate limit and record if allowed (atomic operation)
 * 
 * @param priority - Notification priority level
 * @returns true if the notification was allowed and recorded
 */
export const checkAndRecord = async (priority: NotificationPriority): Promise<boolean> => {
  const allowed = await checkRateLimit(priority);
  if (allowed) {
    await recordSent(priority);
  }
  return allowed;
};

/**
 * Get current rate limit status (useful for debugging/UI)
 */
export const getRateLimitStatus = async (): Promise<{
  hourlyUsed: { medium: number; low: number };
  dailyUsed: { medium: number; low: number };
  limits: typeof RATE_LIMITS;
}> => {
  const store = await loadStore();
  const now = Date.now();
  const hourAgo = now - HOUR_MS;
  const dayAgo = now - DAY_MS;
  
  const hourMedium = store.entries.filter(
    (e) => e.timestamp > hourAgo && e.priority === 'medium'
  ).length;
  const hourLow = store.entries.filter(
    (e) => e.timestamp > hourAgo && e.priority === 'low'
  ).length;
  
  const dayMedium = store.entries.filter(
    (e) => e.timestamp > dayAgo && e.priority === 'medium'
  ).length;
  const dayLow = store.entries.filter(
    (e) => e.timestamp > dayAgo && e.priority === 'low'
  ).length;
  
  return {
    hourlyUsed: { medium: hourMedium, low: hourLow },
    dailyUsed: { medium: dayMedium, low: dayLow },
    limits: RATE_LIMITS,
  };
};

/**
 * Clear all rate limit entries (useful for testing)
 */
export const clearAll = async (): Promise<void> => {
  memoryCache = { entries: [], lastPruned: Date.now() };
  await AsyncStorage.removeItem(STORAGE_KEY);
};

/**
 * Get remaining quota for today
 */
export const getRemainingQuota = async (priority: NotificationPriority): Promise<{
  hourly: number;
  daily: number;
}> => {
  if (priority === 'high') {
    return { hourly: Infinity, daily: Infinity };
  }
  
  const { hourCount, dayCount } = await getCounts(priority);
  const limits = priority === 'low' ? RATE_LIMITS.low : RATE_LIMITS.nonCritical;
  
  return {
    hourly: Math.max(0, limits.perHour - hourCount),
    daily: Math.max(0, limits.perDay - dayCount),
  };
};
